import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {AxiosResponse} from 'axios';
import {useStore} from 'effector-react';
import {useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {validateCodesByArray, validateCodeWithHsn} from 'api';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {CURRENCY} from 'core/constants/currency';
import {IS_NG} from 'core/constants/env';
import {$doesInvoiceExist} from 'models/documents/invoices/common/init';
import {
    $initalLineValuesHolder,
    $invoiceCreationPage,
    $invoiceLineChargeData,
    $invoiceLinesErrors,
    $invoiceLinesHolder,
    $invoiceLinesHolderCodesTotal,
    $invoiceLineValues,
    addInvoiceLineValues,
    invoiceCreationPage,
    setInitalLineValuesHolder,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    updateAllDataFromInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';

import {$currentScannedGtin, setCurrentScannedGtin} from 'models/documents/invoices/details';
import {InvoiceLine} from 'types/invoices';
import {CodeResponseType} from 'types/validation';
import {invoiceValidationWithTransportCodes} from 'utils/markingCodes';

import {ControlSection} from '../../ControlSection';

import {isStandartCategory, VAT_CATEGORY} from '../../helpers/consts';
import {useCodeScanner} from '../../hooks/useCodeScanner';
import {useCombinedData2} from '../../hooks/useCombinedData2';

import {useLineCalculations} from '../../hooks/useLineCalculations';
import {
    ParsedDataExpected,
    XlsxFileInvalidRow,
    XlsxFileParsedData,
} from '../../hooks/useParseInvoiceLinesXlsx';

import {ModalDuplicateLines} from './components/ModalDuplicateLines';
import {ModalFormComponent, ModalFormReadonly} from './components/ModalForm';
import {ModalGroupByGtin} from './components/ModalGroupByGtin';
import {ModalParseLinesFromXlsx} from './components/ModalParseLinesFromXlsx';
import {
    SimpleTable,
    SimpleTablePagination,
    PaginationControlsProps,
} from './components/SimpleTable';
import {
    AddInvoiceLineForm,
    ButtonGroup,
    Container,
    HeaderGroupItem,
    Headline,
    Highlighted,
    RequiredTag,
    Root,
    SectionDescription,
} from './styled';
import {createValidationContext, lineSchema} from './validation';

interface InvoiceLineData extends ParsedDataExpected {
    serviceCategory: {label: string; value: string};
    gtin: string;
    identityCode: string;
    productName: string;
}

export const fieldsToAppend: InvoiceLine = {
    description: '',
    code: '',
    gtin: '',
    qty: '',
    price: '',
    lines: '',
    vatCategory: {label: '', value: ''},
    vat: '',
    lineAmountWVat: '',
    default: true,
    isHsn: 'HS',
    serviceCategory: undefined,
    copyActions: {
        allowToCopy: false,
        allowToDuplicateDown: false,
        allowToPaste: true,
    },
    isMarkedItem: false,
};

export const processInvoiceLinesWithCombinedCodes = (lines: InvoiceLine[]): InvoiceLine[] => {
    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
    const result: InvoiceLine[] = [];
    let index = 0;

    for (const line of lines) {
        const addCharges = line['charges']
            ? toLowerAndTrim(line['charges'].isCharge) === 'true'
                ? Number(line.charges.amountXaf)
                : -Number(line.charges.amountXaf)
            : 0;

        const qty = 1;
        const lines = qty * (Number(line['price']) + addCharges);
        const lineAmountWVat = (Number(line.vat) / 100) * lines + lines;

        if (!line.combinedCodes || line.combinedCodes.length === 0) {
            result.push(line);
            continue;
        }

        const {combinedCodes, ...baseLine} = line;
        const [firstCode, ...remainingCodes] = combinedCodes;

        result.push({
            ...baseLine,
            code: firstCode,
            qty: '1',
            lines: lines.toFixed(2),
            lineAmountWVat: lineAmountWVat.toFixed(2),
            ...(remainingCodes.length > 0 && {combinedCodeId: index.toString()}),
        });

        for (const code of remainingCodes) {
            result.push({
                ...baseLine,
                code,
                qty: '1',
                lines: lines.toFixed(2),
                lineAmountWVat: lineAmountWVat.toFixed(2),
                combinedCodeId: index.toString(),
            });
        }
        index++;
    }

    return result;
};

export const InvoiceLinesTab = () => {
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const currentPageIndex = useStore($invoiceCreationPage);
    const invoiceLineValues = useStore($invoiceLineValues);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const invoiceLineChargeData = useStore($invoiceLineChargeData);
    const initalLineValuesHolder = useStore($initalLineValuesHolder);
    const [allowSubmitButton, setAllowSubmitButton] = useState<boolean>(false);
    const [xlsxErrors, setXlsxErrors] = useState<XlsxFileInvalidRow>({});
    const [combineByGtin, setCombineByGtin] = useState(false);
    const [dataParsingPending, setDataParsingPending] = useState(false);
    const invoiceLinesHolderCodesTotal = useStore($invoiceLinesHolderCodesTotal);

    const [paginationProps, setPaginationProps] = useState<PaginationControlsProps | null>(null);

    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const [scanShow, setScanShow] = React.useState(false);
    const [scanPending, setScanPending] = React.useState(false);

    const resetCombineByGtin = useCallback(() => {
        setCurrentScannedGtin(null);
        setCombineByGtin(false);
    }, []);

    useEffect(() => {
        if (!combineByGtin) {
            setCurrentScannedGtin(null);
        }
    }, [combineByGtin]);

    const convertScanToInvoiceView = (
        scan: CodeResponseType['info'],
    ): Partial<InvoiceLine & {codeGtin: string}> => ({
        code: scan.identityCode,
        gtin: scan.gtin,
        qty: '1',
        description: scan.productName,
        codeGtin: scan.identityCode,

        price: '',
        lines: '',
        vatCategory: {label: '', value: ''},
        vat: '',
        lineAmountWVat: '',
        isHsn: 'HS',
        serviceCategory: {label: scan.hsnDescription ?? scan.hsnCode, value: scan.hsnCode},
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: true,
        },
        isMarkedItem: true,
    });

    const DEFAULT_LINE_VALUES: Partial<InvoiceLine> = {
        description: '',
        code: '',
        gtin: '',
        qty: '',
        isHsn: 'HS',
        serviceCategory: {label: '', value: ''},
        isMarkedItem: false,
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: false,
        },
    };

    const handleEnterFromLine = (response: CodeResponseType['info']) => {
        const {description, code, gtin, serviceCategory} = convertScanToInvoiceView(response);
        setInitalLineValuesHolder({
            ...DEFAULT_LINE_VALUES,
            ...(code &&
                gtin && {
                    qty: '1',
                    description,
                    code,
                    gtin,
                    serviceCategory,
                    isMarkedItem: true,
                    copyActions: {
                        allowToCopy: false,
                        allowToDuplicateDown: false,
                        allowToPaste: false,
                    },
                }),
        });
        setValue('description', description);
        setValue('qty', '1');
        setValue('isHsn', 'HS');
        setValue('serviceCategory', serviceCategory);
    };

    const clearCodeAndDescription2 = () => {
        setValue('codeGtin', '');
        setValue('description', '');
        setValue('serviceCategory', '');
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
    };

    const {onEnterCode, codeGtinHolder} = useCodeScanner({
        combineByGtin,
        handleEnterFromLine,
        clearCodeAndDescription: clearCodeAndDescription2,
        setScanPending,
    });

    const formContext = useMemo(() => createValidationContext(), []);

    const {
        control,
        handleSubmit,
        trigger,
        setValue,
        getValues,
        reset,
        formState: {errors},
    } = useForm({
        defaultValues: {
            description: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: undefined,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
        },
        resolver: yupResolver(lineSchema),
        context: formContext,
    });

    const {fields, remove, append} = useFieldArray<Partial<InvoiceLine> & {default?: boolean}>({
        control,
        name: 'test',
    });

    useEffect(() => {
        if (fields.length > 1 && fields[0].default) remove(0);
        if (!fields.length && !doesInvoiceExist) {
            append(fieldsToAppend);
        }
    }, [fields]);

    const onSubmit = (data: any) => {
        setInvoiceLinesHolder([
            {
                ...data,
                isMarkedItem: !initalLineValuesHolder ? false : initalLineValuesHolder.isMarkedItem,
                ...(invoiceLineChargeData && {charges: invoiceLineChargeData}),
                ...(initalLineValuesHolder && {
                    code: initalLineValuesHolder.code,
                    gtin: initalLineValuesHolder.gtin,
                }),
            },
        ]);
        reset({
            description: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: undefined,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
        });
        codeGtinHolder.current = '';
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
        setInvoiceLineChargeData(null);
    };

    const columnNames: Record<string, {id: string; label: string}> = {
        lineId: {
            id: 'lineId',
            label: '№',
        },
        description: {
            id: 'description',
            label: t('productName'),
        },
        codeGtin: {
            id: 'codeGtin',
            label: t('markingCode'),
        },
        serviceCategory: {
            id: 'serviceCategory',
            label: t('serviceCategory'),
        },
        qty: {
            id: 'qty',
            label: t('qty'),
        },
        price: {
            id: 'price',
            label: t('price'),
        },
        charges: {
            id: 'charges',
            label: t('charges'),
        },
        lines: {
            id: 'lines',
            label: t('lines'),
        },
        vatCategory: {
            id: 'vatCategory',
            label: t('vatCategory'),
        },
        vat: {
            id: 'vat',
            label: t('vat'),
        },
        lineAmountWVat: {
            id: 'lineAmountWVat',
            label: t('lineAmountWVat', {currency: CURRENCY}),
        },
        control: {
            id: 'control',
            label: '',
        },
    };
    const columnsSettings = [
        {
            id: columnNames.lineId.id,
            header: columnNames.lineId.label,
            width: 48,
        },
        {
            id: columnNames.description.id,
            header: (
                <div>
                    <HeaderGroupItem>
                        {t('nameOfProduct')} {<RequiredTag>*</RequiredTag>}
                    </HeaderGroupItem>
                    <div>{t('description')}</div>
                </div>
            ),
            width: 180 + 32,
        },
        {
            id: columnNames.codeGtin.id,
            header: (
                <div>
                    {t('markingCode')}
                    <br />
                    {t('gtin')}
                </div>
            ),
            width: 280 + 32,
            innerContainerStyles: 'width: 100%;',
        },
        {
            id: columnNames.serviceCategory.id,
            header: (
                <HeaderGroupItem>
                    {columnNames.serviceCategory.label} {IS_NG && <RequiredTag>*</RequiredTag>}
                </HeaderGroupItem>
            ),
            width: 174 + 32,
        },
        {
            id: columnNames.qty.id,
            header: <HeaderGroupItem>QTY {<RequiredTag>*</RequiredTag>}</HeaderGroupItem>,
            width: 52 + 32,
        },
        {
            id: columnNames.price.id,
            header: (
                <HeaderGroupItem>
                    {columnNames.price.label} ({CURRENCY}) {<RequiredTag>*</RequiredTag>}
                </HeaderGroupItem>
            ),
            width: 90 + 32,
        },
        {
            id: columnNames.charges.id,
            header: 'Charges',
            width: 160 + 32,
        },
        {
            id: columnNames.lines.id,
            header: `${columnNames.lines.label} (${CURRENCY})`,
            width: 100 + 32,
        },
        {
            id: columnNames.vatCategory.id,
            header: (
                <HeaderGroupItem>
                    {columnNames.vatCategory.label} {<RequiredTag>*</RequiredTag>}
                </HeaderGroupItem>
            ),
            width: 118 + 32,
        },
        {
            id: columnNames.vat.id,
            header: columnNames.vat.label,
            width: 60 + 32,
        },
        {
            id: columnNames.lineAmountWVat.id,
            header: `${columnNames.lineAmountWVat.label}`,
            width: 100 + 32,
        },
        ...(doesInvoiceExist
            ? []
            : [
                  {
                      id: columnNames.control.id,
                      header: '',
                      width: 50,
                  },
              ]),
    ];
    const columns = useMemo(() => mapTableColumns(columnsSettings), [columnsSettings]);

    const {mappedFields2} = useCombinedData2({
        control,
        t: translation,
        onEnterCode,
        trigger,
        getValues,
        setValue,
        errors,
    });

    const [skipPageReset, setSkipPageReset] = useState(false);

    const {combineValues, calculateLineAmountWVat} = useLineCalculations();
    const updateMyData = (rowIndex: number, columnId: string, value: string | object | null) => {
        setSkipPageReset(true);
        updateInvoiceLinesHolder({index: rowIndex, data: {columnId, value: value as any}});
        if (columnId === 'price' || columnId === 'qty') {
            combineValues(rowIndex);
            calculateLineAmountWVat(rowIndex);
        }
        if (columnId === 'vatCategory' || columnId === 'vat') {
            calculateLineAmountWVat(rowIndex, columnId);
        }
    };

    useEffect(() => {
        setSkipPageReset(false);
    }, [invoiceLinesHolder]);

    const mappedData = useMemo(
        () =>
            invoiceLinesHolder.map((item) => ({
                ...item,
                serviceCategory: item.serviceCategory?.value ?? '',
                vatCategory: item.vatCategory?.value ?? '',
            })),
        [invoiceLinesHolder, invoiceLineChargeData],
    );

    const isNextButtonDisabled = useMemo(
        () => !invoiceLineValues?.length || $invoiceLinesErrors.getState().length > 0,
        [invoiceLineValues, $invoiceLinesErrors],
    );

    const [showXlsxModal, setShowXlsxModal] = useState(false);
    const onCloseXlsxModal = () => {
        setShowXlsxModal(false);
        resetCombineByGtin();
    };
    function onShowXlsxModal() {
        setShowXlsxModal(true);
    }

    const datToStore = (data: InvoiceLineData[]) =>
        data.map((item, index) => {
            const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
            const {amount, baseAmount, charges, rate, reason, price, vat} = item;

            const addCharges =
                toLowerAndTrim(charges) === 'charge' ? Number(amount) : -Number(amount);
            const lines = Number(price) + addCharges;
            const lineAmountWVat = (Number(vat) / 100) * lines + lines;
            return {
                isMarkedItem: true,
                copyActions: {
                    allowToCopy: true,
                    allowToDuplicateDown: false,
                    allowToPaste: false,
                },
                code: item.identityCode,
                gtin: item.gtin,
                qty: '1',
                description: item.productName,

                price: item?.price?.toString(),
                lines: lines.toFixed(2),
                vatCategory: VAT_CATEGORY.find((i) => i.value === item?.category) ?? {
                    label: '',
                    value: '',
                },
                vat: item?.vat?.toString(),
                lineAmountWVat: lineAmountWVat.toFixed(2),
                isHsn: 'HS',
                serviceCategory: item.serviceCategory ? item.serviceCategory : undefined,
                ...(!!charges && {
                    charges: {
                        discountRateAndBaseAmount: !!(baseAmount && rate),
                        ...(!!(baseAmount && rate) && {baseAmount: Number(baseAmount)}),
                        ...(!!(baseAmount && rate) && {ratePercent: Number(rate)}),
                        amountXaf: Number(amount),
                        isCharge: toLowerAndTrim(charges) === 'charge' ? 'true' : 'false',
                        reason: reason ?? '',
                    },
                }),
            };
        });

    async function onDataParsed(data: XlsxFileParsedData) {
        if (!data || !data.data) {
            return;
        }

        try {
            setDataParsingPending(true);
            const response = await validateCodesByArray(
                data.data?.map((item) => item?.markingCode ?? '') ?? [],
            );

            const errors = Object.entries(response.data.errors);
            const errorsIndexes = errors.reduce((acc, [index, error]) => {
                acc[index] = {
                    error: [
                        error.map((e: {code: string; message: string}) => e.message).join(', '),
                    ],
                };
                return acc;
            }, {} as XlsxFileInvalidRow);
            if (Object.keys(errorsIndexes).length > 0) {
                setXlsxErrors(errorsIndexes);
                return;
            }

            if (combineByGtin) {
                const responses = response.data.data.map((item) => item.info);
                const fullfilledResponsesWithGroup = responses.reduce((acc, item, index) => {
                    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
                    const {gtin, identityCode, productName, hsnCode, hsnDescription} = item;

                    const {
                        amount,
                        baseAmount,
                        charges,
                        rate,
                        reason,
                        price,
                        vat,
                        category,
                    } = (data.data as ParsedDataExpected[])[index];

                    const hashKeyObject = {
                        gtin,
                        price,
                        charges:
                            typeof charges === 'string'
                                ? charges.toLowerCase()
                                : JSON.stringify(charges).toLowerCase(),
                        rate,
                        reason,
                        vat,
                        category,
                    };

                    const hashKey = JSON.stringify(hashKeyObject);

                    if (acc[hashKey]) {
                        const addCharges = acc[hashKey]['charges']
                            ? toLowerAndTrim(acc[hashKey]['charges'].isCharge) === 'true'
                                ? Number(acc[hashKey].charges.amountXaf)
                                : -Number(acc[hashKey].charges.amountXaf)
                            : 0;

                        const qty = Number(acc[hashKey]['qty']) + 1;
                        const lines = qty * Number(acc[hashKey]['price']) + addCharges;
                        const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                        acc[hashKey]['combinedCodes'] = [
                            ...acc[hashKey]['combinedCodes'],
                            identityCode,
                        ];
                        acc[hashKey]['qty'] = qty.toString();
                        acc[hashKey]['lines'] = lines.toFixed(2);
                        acc[hashKey]['lineAmountWVat'] = lineAmountWVat.toFixed(2);
                        return acc;
                    }

                    const addCharges = charges
                        ? toLowerAndTrim(charges) === 'charge'
                            ? Number(amount)
                            : -Number(amount)
                        : 0;
                    const lines = Number(price) + addCharges;
                    const lineAmountWVat = (Number(vat) / 100) * lines + lines;

                    return {
                        ...acc,
                        [hashKey]: {
                            isMarkedItem: true,
                            copyActions: {
                                allowToCopy: true,
                                allowToDuplicateDown: false,
                                allowToPaste: false,
                            },
                            code: '',
                            gtin,
                            qty: '1',
                            description: productName,

                            price: price?.toString(),
                            lines: lines.toFixed(2),
                            vatCategory: VAT_CATEGORY.find((i) => i.value === category) ?? {
                                label: '',
                                value: '',
                            },
                            vat: vat?.toString(),
                            lineAmountWVat: lineAmountWVat.toFixed(2),
                            isHsn: 'HS',
                            serviceCategory: {
                                label: hsnDescription ?? hsnCode,
                                value: hsnCode,
                            },
                            combinedCodes: [identityCode],
                            ...(!!charges && {
                                charges: {
                                    discountRateAndBaseAmount: !!(baseAmount && rate),
                                    ...(!!(baseAmount && rate) && {baseAmount: Number(baseAmount)}),
                                    ...(!!(baseAmount && rate) && {ratePercent: Number(rate)}),
                                    amountXaf: Number(amount),
                                    isCharge:
                                        toLowerAndTrim(charges) === 'charge' ? 'true' : 'false',
                                    reason: reason ?? '',
                                },
                            }),
                        },
                    };
                }, {} as Record<string, any>);

                const dataToStore = Object.values(fullfilledResponsesWithGroup) as InvoiceLine[];

                setInvoiceLinesHolder(dataToStore);
                onCloseXlsxModal();
                return;
            }

            const fullfilledResponses = response.data.data.map((res, index) => {
                const {gtin, identityCode, productName, hsnCode, hsnDescription} = res.info;
                const dummy = (data.data as ParsedDataExpected[])[index];
                return {
                    ...dummy,
                    gtin,
                    identityCode,
                    productName,
                    serviceCategory: {
                        label: hsnDescription ?? hsnCode,
                        value: hsnCode,
                    },
                };
            });

            const dataToStore = datToStore(fullfilledResponses) as InvoiceLine[];
            setInvoiceLinesHolder(dataToStore);
            onCloseXlsxModal();
        } catch (error) {
            console.error(error);
        } finally {
            setDataParsingPending(false);
        }
    }

    return (
        <Root>
            <ModalFormComponent getValues={getValues} setValue={setValue} trigger={trigger} />
            <ModalFormReadonly />
            <ModalDuplicateLines />
            <ModalGroupByGtin />
            <Container>
                <Headline>
                    <h3>
                        {t('invoiceLines')}
                        {!doesInvoiceExist && (
                            <Highlighted>
                                {`(${invoiceLinesHolderCodesTotal} ${t('itemsAdded')})`}
                            </Highlighted>
                        )}
                    </h3>
                    <SectionDescription>
                        <span>{doesInvoiceExist ? '' : t('addGoods.1')}</span>
                    </SectionDescription>
                    <ButtonGroup style={IS_NG ? {justifyContent: 'flex-end'} : undefined}>
                        {!doesInvoiceExist && (
                            <>
                                {!IS_NG && (
                                    <Button
                                        variant={ButtonVariant.Outline}
                                        onClick={onShowXlsxModal}
                                    >
                                        {t('xlsxModal.upload')}
                                    </Button>
                                )}
                                <Button
                                    style={IS_NG ? {maxWidth: '156px'} : undefined}
                                    onClick={() => setScanShow(true)}
                                >
                                    {translation('components.scan.button')}
                                    <Icon source={ScanIcon} width={20} height={20} />
                                </Button>
                            </>
                        )}
                    </ButtonGroup>
                </Headline>
                <AddInvoiceLineForm onSubmit={handleSubmit(onSubmit)}>
                    {!doesInvoiceExist && (
                        <Table
                            hoverableRow={false}
                            tableViewExtraSettings={{
                                tdThPadding: '5px 10px',
                                overflowX: 'visible',
                            }}
                            cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                            hideSetting={true}
                            isLoading={false}
                            columns={columns}
                            data={mappedFields2}
                        />
                    )}

                    {!doesInvoiceExist && (
                        <Button
                            style={{width: 'fit-content', position: 'sticky', left: 0}}
                            size="sm"
                            variant={ButtonVariant.Secondary}
                            disabled={allowSubmitButton}
                            type="submit"
                        >
                            {translation('pages.documents.invoices.create.buttons.addToInvoice')}
                        </Button>
                    )}

                    <SimpleTable
                        hideHeaders={!doesInvoiceExist}
                        columns={columns}
                        data={mappedData}
                        onPaginationReady={setPaginationProps}
                        updateMyData={updateMyData}
                        skipPageReset={skipPageReset}
                        tableViewExtraSettings={{
                            tdThPadding: '5px 10px',
                            overflowX: 'visible',
                        }}
                        cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                    />
                </AddInvoiceLineForm>
                {paginationProps && mappedData.length > 0 && (
                    <div>
                        <SimpleTablePagination {...paginationProps} />
                    </div>
                )}
            </Container>
            <ControlSection
                nextIsDisabled={false}
                onSubmit={(nextIndex: number) => {
                    if (!invoiceLinesHolder.length) {
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <ul style={{margin: '6px 0'}}>
                                    <li>{t('errors.noLines')}</li>
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    if ($invoiceLinesErrors.getState().length) {
                        const limitErrors = [...$invoiceLinesErrors.getState()];
                        limitErrors.length > 5 ? (limitErrors.length = 5) : limitErrors;
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <strong>{t('errors.emptyFields')}</strong>
                                <ul style={{margin: '6px 0'}}>
                                    {limitErrors.map((e) => {
                                        const {index, fields} = e;
                                        return (
                                            <li key={index}>
                                                {t('errors.line')} {index + 1}: {fields.join(', ')}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    const processedLines = processInvoiceLinesWithCombinedCodes(invoiceLinesHolder);

                    addInvoiceLineValues(processedLines);
                    invoiceCreationPage(nextIndex);
                }}
                nextIndex={currentPageIndex + 1}
            />
            <QRScannerModal
                open={scanShow}
                pending={scanPending}
                onClose={() => {
                    resetCombineByGtin();
                    setScanShow(false);
                }}
                onScan={onEnterCode}
                onManual={onEnterCode}
                allowToGroupByGtin={true}
                combineByGtin={combineByGtin}
                setCombineByGtin={setCombineByGtin}
            />
            <ModalParseLinesFromXlsx
                show={showXlsxModal}
                onClose={onCloseXlsxModal}
                onDataParsed={onDataParsed}
                xlsxErrors={xlsxErrors}
                setXlsxErrors={setXlsxErrors}
                combineByGtin={combineByGtin}
                setCombineByGtin={setCombineByGtin}
                dataParsingPending={dataParsingPending}
            />
        </Root>
    );
};
